import { Button, Collapse, DatePicker, Form, Grid, Input, Popconfirm, Radio, Space, theme, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, FileUploadSpec } from '../../../../constants';
import {
  FileInfo,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput
} from '../../../../generated';
import { Country } from '../../../../types/country';
import { IdCaseFileUploadButtonWithPreviewContainer } from '../../../shared/file-upload-button-with-preview/id-case-file-upload-button-with-preview.container';
import { AzureBlobResponse } from '../../../shared/file-upload/azure-upload';
import { CountryDropdownContainer } from '../../../shared/country-dropdown/country-dropdown-container';
import { useBeforeUnload } from '../shared/components/custom-hooks';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface PassportDetailsProps {
  applicationPassportDetails: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails;
  countryData: Country[];
  onSave: (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput) => Promise<void>;
  onSaveAndContinue: (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput) => Promise<void>;
  onWithdrawRequest: () => Promise<void>;
  caseId: string;
  fetchFilesData: () => any;
  onUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
}

const RequiredTitle = ({ title }: { title: string }) => (
  <Space direction="horizontal" size={3} style={{ alignItems: 'start' }}>
    <div style={{ fontSize: '14px', color: '#ff4d4f', fontFamily: 'SimSun,sans-serif' }}>*</div>
    <Typography.Title level={5}>{title}</Typography.Title>
  </Space>
);

export const PassportDetails: React.FC<PassportDetailsProps> = (props) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isExpirationButtonVisible, setIsExpirationButtonVisible] = useState<boolean>(props.applicationPassportDetails?.isPassportExpirationDateVisible ?? true);
  const [isTranslationButtonVisible, setIsTranslationButtonVisible] = useState<boolean>(props.applicationPassportDetails?.isPassportInLatinCharacters ?? true);
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);

  const fetchFiles = async () => {
    const fileData: FileInfo[] = await props.fetchFilesData();
    return fileData;
  };
  useEffect(() => {
    const fileData = fetchFiles();
    fileData.then((files) => {
      files.forEach((file) => {
        if (file.name.includes(FileUploadSpec.Passport.fileName)) {
          form.setFieldValue(['passportUpload'], 'uploaded');
        }
        if (file.name.includes(FileUploadSpec.PassportExpirationPage.fileName)) {
          form.setFieldValue(['passportExpirationUpload'], 'uploaded');
        }
        if (file.name.includes(FileUploadSpec.PassportTranslation.fileName)) {
          form.setFieldValue(['passportTranslationUpload'], 'uploaded');
        }
        if (file.name.includes(FileUploadSpec.Photo.fileName)) {
          form.setFieldValue(['photo'], 'uploaded');
        }
      });
    });
  }, []);

  const warningItems = [
    {
      key: '1',
      label: (
        <p>
          <ExclamationCircleOutlined /> Important Information
        </p>
      ),
      children: (
        <Typography.Text style={{ fontSize: '13px' }}>
          Before uploading, be sure to read the Passport information on the <a href="https://www.ecfmg.org/cvhp/" target="_blank" rel="noopener noreferrer">CVHP Instruction Page</a> for details and requirements.
        </Typography.Text>
      )
    },
    {
      key: '2',
      label: (
        <p>
          <ExclamationCircleOutlined /> Important Information
        </p>
      ),
      children: (
        <Typography.Text style={{ fontSize: '13px' }}>
          Before uploading, be sure to read the Photograph information on the <a href="https://www.ecfmg.org/cvhp/" target="_blank" rel="noopener noreferrer">CVHP Instruction Page</a> for details and requirements.
        </Typography.Text>
      )
    }
  ];

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const safeParseBoolean = (value: string | null | undefined): boolean | null => {  
    if (!value) {
      return null;  
    }
    try {  
      const parsed = JSON.parse(value);  
      return typeof parsed === 'boolean' ? parsed : null;  
    } catch (error) {  
      console.error(`Error parsing boolean value: ${value}`, error);  
      return null;  
    }  
  }; 

  const formatPassportDate = (date: any) => {
    return date ? dayjs(date).utc().format(APOLLO_DATE_FORMAT) : null;
  };

  const onSave = async () => {  
    clearValidationErrors()  
    setIsFocus(false);  
    const values = form.getFieldsValue();  
    const formattedValues = {  
      passportNumber: values.passportNumber,
      passportCountry: values.passportCountry,  
      passportIssuedAt: formatPassportDate(values.passportIssuedAt),
      passportExpiresAt: formatPassportDate(values.passportExpiresAt),
      isPassportInLatinCharacters: safeParseBoolean(values.isPassportInLatinCharacters),  
      isPassportExpirationDateVisible: safeParseBoolean(values.isPassportExpirationDateVisible)  
    };  
    await props.onSave(formattedValues);
  };

  const clearValidationErrors = () => {
    const fields = form.getFieldsError().map((field) => ({
      name: field.name,
      errors: [],
    }));
    console.log(form.getFieldsError())
    form.setFields(fields); // Clears validation errors but keeps current values
  };

  const onSubmit = async () => {
    setIsUpdatingApplicant(true);
    form
      .validateFields()
      .then(async (values) => {
        form.submit();

        const formattedValues: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput = {
          passportNumber: values.passportNumber,
          passportCountry: values.passportCountry,
          passportIssuedAt: formatPassportDate(values.passportIssuedAt),
          passportExpiresAt: formatPassportDate(values.passportExpiresAt),
          isPassportInLatinCharacters: safeParseBoolean(values.isPassportInLatinCharacters),
          isPassportExpirationDateVisible: safeParseBoolean(values.isPassportExpirationDateVisible)
        };
        await props.onSaveAndContinue(formattedValues);
        setIsUpdatingApplicant(false);
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
        setIsUpdatingApplicant(false);
        console.log('Form Error', error);
      });
  };

  //block-if-not-saved
  useBeforeUnload(isFocus, 'Changes that you made may not be saved.');

  const formFocusHandler = () => {
    setIsFocus(true);
  };
  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          width: screens.xs ? '100%' : '70%',
          padding: screens.xs ? '24px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          marginTop: '24px',
          flexDirection: 'column',
          alignItems: 'baseline'
        }}
      >
        <h2>Passport Details</h2>
        <Form
          form={form}
          onFocus={formFocusHandler}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'baseline',
            flexDirection: 'column'
          }}
          initialValues={{
            ...props.applicationPassportDetails,
            passportIssuedAt: props.applicationPassportDetails?.passportIssuedAt ? dayjs(props.applicationPassportDetails?.passportIssuedAt).utc() : null,
            passportExpiresAt: props.applicationPassportDetails?.passportExpiresAt ? dayjs(props.applicationPassportDetails?.passportExpiresAt).utc() : null
          }}
          layout="vertical"
        >
          <CountryDropdownContainer
            name={['passportCountry']}
            style={{ width: screens.xs ? '100%' : 410 }}
            label="Country that Issued Passport"
            rules={[{ required: true, message: 'Please select a country' }]}
            showSearch
            optionFilterProp="children"
          />
          <div style={{ display: 'flex', flexDirection: screens.xs ? 'column' : 'row', gap: 10 }}>
            <Form.Item
              name={['passportIssuedAt']}
              label="Passport Issue Date"
              validateTrigger={['onChange', 'onBlur']}
              rules={[
                {
                  required: true,
                  message: 'Please select passport issue date'
                }
              ]}
            >
              <DatePicker
                format={DATE_FORMAT.toUpperCase()}
                style={{ width: screens.xs ? '100%' : 200 }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
            <Form.Item
              name={['passportExpiresAt']}
              label="Passport Expiration Date"    
              validateTrigger={['onChange', 'onBlur']}          
              rules={[
                {
                  required: true,
                  message: 'Please select passport expiration date'
                }
              ]}
            >
              <DatePicker
                format={DATE_FORMAT.toUpperCase()}
                style={{ width: screens.xs ? '100%' : 200 }}
                disabledDate={(current) => {
                  const passportIssuedAt = form.getFieldValue('passportIssuedAt');
                  const minDate = passportIssuedAt ? dayjs(passportIssuedAt).utc() : dayjs().utc();
                  return current && current <= minDate.startOf('day');
                }}
              />
            </Form.Item>
          </div>
          <Form.Item
            label="Passport Number"
            name={['passportNumber']}
            rules={[
              { required: true, message: 'Please enter passport number' },
              {
                validator: (_, value) =>
                  /^[A-Z]+$/.test(value)
                    ? Promise.reject(new Error('Invalid Passport Number'))
                    : Promise.resolve(),
              },
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Input
              maxLength={9}
              style={{ width: screens.xs ? '100%' : 340 }}
              placeholder="Passport Number"
              onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
            />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Form.Item
              name={['passportUpload']}
              rules={[
                {
                  required: true,
                  message: 'Please upload the passport'
                }
              ]}
            >
              <RequiredTitle title="Passport Upload" />
              <Collapse size="small" ghost items={[warningItems[0]]} />
              <IdCaseFileUploadButtonWithPreviewContainer
                buttonText={'Upload Passport'}
                permittedContentTypes={FileUploadSpec.Passport.permittedContentTypes}
                permittedExtensions={FileUploadSpec.Passport.permittedExtensions}
                caseId={props.caseId}
                fileName={FileUploadSpec.Passport.fileName}
                onUploadSuccess={(response) => {
                  form.setFieldsValue({ passportUpload: 'uploaded' });
                  return props.onUploadSuccess(response, FileUploadSpec.Passport.fieldName);
                }}
                isShowFileName={false}
                cropperProps={{ aspect: 3 / 4 }}
              />
            </Form.Item>
            <div style={{ display: 'flex', flexDirection: screens.xs ? 'column' : 'row' }}>
              <Form.Item
                getValueProps={(value) => ({
                  value: !!(value === 'true')
                })}
                style={{ marginTop: '25px' }}
                label="Expiration Date Visible in Scan?"
                valuePropName="value"
                name={['isPassportExpirationDateVisible']}
                rules={[
                  {
                    required: true
                  }
                ]}
                validateTrigger={['onChange', 'onBlur']}
                labelCol={{ span: 24 }}
              >
                <div>
                  <Radio.Group
                    style={{ marginLeft: '8px' }}
                    onChange={(e) => setIsExpirationButtonVisible(e.target.value)}
                    defaultValue={props.applicationPassportDetails?.isPassportExpirationDateVisible}
                  >
                    <Radio value={false}>No</Radio>
                    <Radio value={true}>Yes</Radio>
                  </Radio.Group>
                </div>
              </Form.Item>
              {screens.xs && !isExpirationButtonVisible && (
                <Form.Item
                  name="passportExpirationUpload"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload the passport expiration page'
                    }
                  ]}
                >
                  <RequiredTitle title="Passport Expiration Page" />
                  <IdCaseFileUploadButtonWithPreviewContainer
                    description={'If the scan of the page with your name and photo does not include the expiration date, you will need to upload a scan of the page that includes your passport expiration date here.'}
                    buttonText={'Upload Expiration Page'}
                    permittedContentTypes={FileUploadSpec.PassportExpirationPage.permittedContentTypes}
                    permittedExtensions={FileUploadSpec.PassportExpirationPage.permittedExtensions}
                    caseId={props.caseId}
                    fileName={FileUploadSpec.PassportExpirationPage.fileName}
                    onUploadSuccess={(response) => {
                      form.setFieldsValue({ passportExpirationUpload: 'uploaded' });
                      return props.onUploadSuccess(response, 'passportExpiration');
                    }}
                    isShowFileName={false}
                    cropperProps={{ aspect: 3 / 4 }}
                  />
                </Form.Item>
              )}
              <Form.Item
                getValueProps={(value) => ({
                  value: !!value
                })}
                style={{ marginTop: '25px', marginLeft: screens.xs ? '0' : '136px' }}
                label="Passport in English?"
                valuePropName="value"
                name={['isPassportInLatinCharacters']}
                rules={[
                  {
                    required: true
                  }
                ]}
                validateTrigger={['onChange', 'onBlur']}
                labelCol={{ span: 24 }}
              >
                <div>
                  <Radio.Group
                    style={{ marginLeft: '8px' }}
                    onChange={(e) => setIsTranslationButtonVisible(e.target.value)}
                    defaultValue={props.applicationPassportDetails?.isPassportInLatinCharacters}
                  >
                    <Radio value={false}>No</Radio>
                    <Radio value={true}>Yes</Radio>
                  </Radio.Group>
                </div>
              </Form.Item>
              {screens.xs && !isTranslationButtonVisible && (
                <Form.Item
                  name="passportTranslationUpload"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload the passport translation'
                    }
                  ]}
                >
                  <RequiredTitle title="English Translation of Passport" />
                  <IdCaseFileUploadButtonWithPreviewContainer
                    // title={'Passport Translation'}
                    description={"If the scan of the page with your name and photo is not in English, you will need to upload an English translation that meets ECFMG's translation requirements."}
                    buttonText={'Upload English Translation'}
                    permittedContentTypes={FileUploadSpec.PassportTranslation.permittedContentTypes}
                    permittedExtensions={FileUploadSpec.PassportTranslation.permittedExtensions}
                    caseId={props.caseId}
                    fileName={FileUploadSpec.PassportTranslation.fileName}
                    onUploadSuccess={(response) => {
                      form.setFieldsValue({ passportTranslationUpload: 'uploaded' });
                      return props.onUploadSuccess(response, FileUploadSpec.PassportTranslation.fieldName);
                    }}
                    isShowFileName={false}
                  />
                </Form.Item>
              )}
            </div>

            {!screens.xs && (
              <div style={{ display: 'flex' }}>
                {!isExpirationButtonVisible && (
                  <Form.Item
                    name="passportExpirationUpload"
                    rules={[
                      {
                        required: true,
                        message: 'Please upload the passport expiration page'
                      }
                    ]}
                  >
                    <RequiredTitle title="Passport Expiration Page" />
                    <IdCaseFileUploadButtonWithPreviewContainer
                      description={"If the scan of the page with your name and photo does not include the expiration date, you will need to upload a scan of the page that includes your passport expiration date here."}
                      buttonText={'Upload Expiration Page'}
                      permittedContentTypes={FileUploadSpec.PassportExpirationPage.permittedContentTypes}
                      permittedExtensions={FileUploadSpec.PassportExpirationPage.permittedExtensions}
                      caseId={props.caseId}
                      fileName={FileUploadSpec.PassportExpirationPage.fileName}
                      onUploadSuccess={(response) => {
                        form.setFieldsValue({ passportExpirationUpload: 'uploaded' });
                        return props.onUploadSuccess(response, 'passportExpiration');
                      }}
                      isShowFileName={false}
                    />
                  </Form.Item>
                )}
                {!isTranslationButtonVisible && (
                  <Form.Item
                    name="passportTranslationUpload"
                    rules={[
                      {
                        required: true,
                        message: 'Please upload the passport translation'
                      }
                    ]}
                  >
                    <RequiredTitle title="English Translation of Passport" />
                    <IdCaseFileUploadButtonWithPreviewContainer
                      // title={'Passport Translation'}
                      description={"If the scan of the page with your name and photo is not in English, you will need to upload an English translation that meets ECFMG's translation requirements."}
                      buttonText={'Upload English Translation'}
                      permittedContentTypes={FileUploadSpec.PassportTranslation.permittedContentTypes}
                      permittedExtensions={FileUploadSpec.PassportTranslation.permittedExtensions}
                      caseId={props.caseId}
                      fileName={FileUploadSpec.PassportTranslation.fileName}
                      onUploadSuccess={(response) => {
                        form.setFieldsValue({ passportTranslationUpload: 'uploaded' });
                        return props.onUploadSuccess(response, FileUploadSpec.PassportTranslation.fieldName);
                      }}
                      isShowFileName={false}
                    />
                  </Form.Item>
                )}
              </div>
            )}

            <Form.Item
              name="photo"
              style={{ marginTop: '25px' }}
              rules={[
                {
                  required: true,
                  message: 'Please upload the photo'
                }
              ]}
            >
              <RequiredTitle title="Photograph" />
              <Collapse size="small" ghost items={[warningItems[1]]} />
              <IdCaseFileUploadButtonWithPreviewContainer
                buttonText={'Upload Photo'}
                permittedContentTypes={FileUploadSpec.Photo.permittedContentTypes}
                permittedExtensions={FileUploadSpec.Photo.permittedExtensions}
                caseId={props.caseId}
                fileName={FileUploadSpec.Photo.fileName}
                onUploadSuccess={(response) => {
                  form.setFieldsValue({ photo: 'uploaded' });
                  return props.onUploadSuccess(response, FileUploadSpec.Photo.fieldName);
                }}
                isShowFileName={false}
                cropperProps={{ aspect: 1 }}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '24px 0px',
          gap: screens.xs ? '10px' : '0',
          width: screens.xs ? '100%' : '70%'
        }}
      >
        <div>
          <Popconfirm
            title={isFocus ? 'Are you sure? Changes you made will not be saved.' : null}
            onConfirm={() => navigate(`/applicant/onboarding/primary-education-details`)}
            disabled={!isFocus}
          >
            <Button
              onClick={() => (isFocus ? null : navigate(`/applicant/onboarding/primary-education-details`))}
              style={{ width: screens.xs ? '80px' : '90%', height: screens.xs ? '50px' : 'auto' }}
            >
              {'<< Back'}
            </Button>
          </Popconfirm>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: '15px'
          }}
        >
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={() => props.onWithdrawRequest()} disabled={isUpdatingApplicant}>
            <Button danger style={{ height: screens.xs ? '50px' : 'auto' }}>
              Withdraw Request
            </Button>
          </Popconfirm>
          <Button onClick={onSave} style={{ height: screens.xs ? '50px' : 'auto' }} loading={isUpdatingApplicant}>
            Save Progress
          </Button>
          <Button type="primary" onClick={() => onSubmit()} style={{ height: screens.xs ? '50px' : 'auto' }} disabled={isUpdatingApplicant}>
            {'Save and Continue >>'}
          </Button>
        </div>
      </div>
    </>
  );
};
